// check if a value is a class (and not an object or array)
export function isClass(target: unknown) {
    if (typeof target === "object" && target !== null) {
        return /^(object|array)$/i.test(target.constructor.name) === false
    } else {
        return false
    }
}

export function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

export function isArrayLike(value: unknown): value is ArrayLike<unknown> {
    return (
        value != null &&
        typeof value === "object" &&
        typeof (value as any).length === "number" &&
        (value as any).length >= 0 &&
        Number.isInteger((value as any).length)
    )
}

export function isPromise(obj: unknown): obj is Promise<unknown> {
    return !!obj && typeof obj === "object" && "then" in obj && typeof obj.then === "function" // more reliable than "obj instanceof Promise" because it's possible to have a Promise from another realm
}

export function removeFromArray<T>(array: T[], toRemove: T) {
    const idx = array.findIndex((x) => x === toRemove)
    if (idx < 0) return false
    array.splice(idx, 1)
    return true
}

export function removeFromArrayByID<IdType, ElemType extends {id: IdType}>(array: ElemType[], toRemove: ElemType) {
    const idx = array.findIndex((x) => x.id == toRemove.id)
    if (idx < 0) return false
    array.splice(idx, 1)
    return true
}

export function removeSubtreeFromArrayByID<IdType, ElemType extends {id: IdType}>(
    array: ElemType[],
    toRemove: ElemType,
    relationFn: (parent: ElemType, child: ElemType) => boolean,
) {
    const idx = array.findIndex((x) => x.id == toRemove.id)
    if (idx >= 0) {
        array.splice(idx, 1)
    }
    const children = array.filter((x) => relationFn(toRemove, x))
    children.forEach((x) => removeSubtreeFromArrayByID(array, x, relationFn))
}

export function insertBefore<T>(arr: T[], after: T, ...items: T[]) {
    const index = arr.indexOf(after)
    if (index < 0) arr.push(...items)
    else arr.splice(index, 0, ...items)
}

export function insertAfter<T>(arr: T[], after: T, ...items: T[]) {
    const index = arr.indexOf(after)
    if (index < 0) arr.push(...items)
    else arr.splice(index + 1, 0, ...items)
}

export function uniqueElements<T>(arr: T[]) {
    return arr.filter((value, index, self) => self.indexOf(value) === index)
}

export function listWithIDsToMap<ObjType extends {id: number}>(arr: ObjType[]) {
    const map = new Map<number, ObjType>()
    arr.forEach((elem) => map.set(elem.id, elem))
    return map
}

export function flatMap<A, B>(arr: A[], fn: (a: A) => B[]): B[] {
    const bs: B[] = []
    arr.map((a) => bs.push(...fn(a)))
    return bs
}

export function appendToMap<K, V>(m: Map<K, V[]>, k: K, v: V) {
    const existing = m.get(k)
    if (existing) existing.push(v)
    else m.set(k, [v])
}

export function mapFirstTrue<A, B>(arr: A[], fn: (a: A) => B) {
    return arr.reduce((prev, cur) => (prev ? prev : fn(cur)), null as any as B)
}

export function filterInPlace<A>(arr: A[], condition: (a: A, idx: number, arr: A[]) => boolean): A[] {
    let i = 0,
        j = 0
    while (i < arr.length) {
        const val = arr[i]
        if (condition(val, i, arr)) arr[j++] = val
        i++
    }
    arr.length = j
    return arr
}

export function parseColor(input: string): [number, number, number] {
    if (input.substr(0, 1) == "#") {
        const collen = (input.length - 1) / 3
        const fact = [17 / 255, 1 / 255, 0.062272 / 255][collen - 1]
        return [
            parseInt(input.substr(1, collen), 16) * fact,
            parseInt(input.substr(1 + collen, collen), 16) * fact,
            parseInt(input.substr(1 + 2 * collen, collen), 16) * fact,
        ]
    } else {
        return [1, 1, 1]
    }
}

export function parseRgbColor(input: string): {r: number; g: number; b: number} {
    if (input.substr(0, 1) == "#") {
        const collen = (input.length - 1) / 3
        const fact = [17 / 255, 1 / 255, 0.062272 / 255][collen - 1]
        return {
            r: parseInt(input.substr(1, collen), 16) * fact,
            g: parseInt(input.substr(1 + collen, collen), 16) * fact,
            b: parseInt(input.substr(1 + 2 * collen, collen), 16) * fact,
        }
    } else {
        return {r: 1, g: 1, b: 1}
    }
}

export function toHex2(d: number) {
    return ("0" + Number(d).toString(16)).slice(-2).toLowerCase()
}

export function colorToString(color: {r: number; g: number; b: number; a?: number} | [number, number, number] | undefined) {
    if (!color) return "#000"
    if (Array.isArray(color)) {
        return `#${toHex2(Math.round(255 * color[0]))}${toHex2(Math.round(255 * color[1]))}${toHex2(Math.round(255 * color[2]))}`
    }
    return `#${toHex2(Math.round(255 * color.r))}${toHex2(Math.round(255 * color.g))}${toHex2(Math.round(255 * color.b))}${color.a ? toHex2(Math.round(255 * color.a)) : ""}`
}

export const hexColorRegex = /^#[A-Fa-f0-9]{6}$/

export function stringToColor(colorString: string): [number, number, number] {
    if (!hexColorRegex.test(colorString)) throw new Error(`Invalid color string: ${colorString}`)

    const r = parseInt(colorString.slice(1, 3), 16) / 255
    const g = parseInt(colorString.slice(3, 5), 16) / 255
    const b = parseInt(colorString.slice(5, 7), 16) / 255

    return [r, g, b]
}

export type KeysOfUnion<T> = T extends any ? keyof T : never

// traverses an object calling valueMapper on each value and keep iterating on the returned value
export function traverseObject(value: unknown, valueMapper: (value: unknown, path: string[]) => unknown, path: string[] = []) {
    value = valueMapper(value, path)
    if (Array.isArray(value)) {
        value.forEach((value, index) => traverseObject(value, valueMapper, [...path, `[${index}]`]))
    } else if (value && typeof value === "object") {
        Object.entries(value).forEach(([key, value]) => traverseObject(value, valueMapper, [...path, key]))
    }
}

export function prettyPrintPath(path: (string | number)[]): string {
    return path.reduce<string>((pathString, segment) => {
        if (typeof segment === "number") {
            return `${pathString}[${segment}]`
        }
        if (pathString === "") {
            return `${segment}`
        }
        return `${pathString}.${segment}`
    }, "")
}

export function compareObjects(
    actual: unknown,
    expected: unknown,
    callback: (actual: unknown, expected: unknown, path: (string | number)[]) => unknown,
    path: (string | number)[] = [],
) {
    if (Array.isArray(expected)) {
        if (Array.isArray(actual)) {
            if (expected.length === actual.length) {
                expected.forEach((expectedValue, index) => compareObjects(actual[index], expectedValue, callback, [...path, index]))
                return
            } else {
                throw new Error(`Mismatched array lengths at path ${prettyPrintPath(path)}: expected ${expected.length}, got ${actual.length}`)
            }
        } else {
            throw new Error(`Expected array at path ${prettyPrintPath(path)}, but got ${typeof actual}`)
        }
    } else if (expected && typeof expected === "object") {
        if (typeof actual !== "object") {
            throw new Error(`Expected object at path ${prettyPrintPath(path)}, but got ${typeof actual}`)
        } else if (actual === null) {
            if (expected) {
                throw new Error(`Expected non-null object at path ${prettyPrintPath(path)}, but got null`)
            }
        } else {
            Object.entries(expected).forEach(([key, expectedValue]) => {
                if (key in actual) {
                    compareObjects((actual as Record<string, unknown>)[key], expectedValue, callback, [...path, key])
                } else {
                    throw new Error(`Missing key "${key}" at path ${prettyPrintPath(path)}`)
                }
            })
            Object.entries(actual).forEach(([key, actualValue]) => {
                if (!(key in expected)) {
                    throw new Error(`Extraneous key "${key}" at path ${prettyPrintPath(path)}`)
                }
            })
            return
        }
    }
    callback(actual, expected, path)
}

export function deepEqual<T>(a: T, b: T, depth?: number): boolean {
    const fieldDepth = depth ? depth - 1 : depth
    const typeA = typeof a
    const typeB = typeof b
    if (typeA === "number" && typeB === "number" && isNaN(a as any) && isNaN(b as any)) return true
    else if (depth === 0) return a === b
    else if (a === b) return true
    else if (a && b && typeA === "object" && typeB === "object") {
        if (Array.isArray(a) && Array.isArray(b)) {
            const length = a.length
            if (length != b.length) return false
            for (let i = length; i-- !== 0; ) {
                if (!deepEqual(a[i], b[i], fieldDepth)) return false
            }
            return true
        } else if (a instanceof Set || b instanceof Set) {
            if (!(a instanceof Set && b instanceof Set)) {
                return false
            }
            if (a.size !== b.size) {
                return false
            }
            for (const item of a) {
                if (!b.has(item)) {
                    return false
                }
            }
            return true
        } else {
            const keys = Object.keys(a as unknown as object) as (keyof T)[]
            const length = keys.length
            if (length !== Object.keys(b as unknown as object).length) return false
            for (const k of keys) {
                if (!(k in (b as unknown as object))) return false
                if (!deepEqual(a[k], b[k], fieldDepth)) return false
            }
            return true
        }
    } else {
        return false
    }
}

export function debugDiff<T>(prefix: string, a: T, b: T, depth?: number, log?: (message: string) => void): void {
    const fieldDepth = depth ? depth - 1 : depth
    if (depth === 0) {
        if (a !== b) {
            log?.(`${prefix}: ${a} != ${b}`)
        }
    } else if (a === b) return
    else if (a && b && typeof a == "object" && typeof b == "object") {
        if (Array.isArray(a) && Array.isArray(b)) {
            const length = a.length
            if (length != b.length) {
                log?.(`${prefix}.length: ${length} != ${b.length}`)
                return
            }
            for (let i = length; i-- !== 0; ) {
                debugDiff(prefix + `[${i}]`, a[i], b[i], fieldDepth)
            }
        } else {
            const keys = Object.keys(a) as KeysOfUnion<T>[]
            const length = keys.length
            if (length !== Object.keys(b).length) {
                log?.(`${prefix}: keys: ${Object.keys(a)} != ${Object.keys(b)}`)
                return
            }
            for (const k of keys) {
                debugDiff(prefix + `.${String(k)}`, a[k], b[k], fieldDepth)
            }
        }
    } else {
        log?.(`${prefix}: ${a} != ${b}`)
    }
}

export function deepCopy<T>(x: T, depth?: number, customCopyFunction?: (key: string | number | symbol, value: any) => any): T {
    if (depth === 0) {
        return x
    } else if (x && typeof x == "object") {
        if (depth !== undefined) --depth
        if (isClass(x)) {
            // don't copy classes, just return the instance
            return x
        } else if (Array.isArray(x)) {
            return x.map((e) => deepCopy(e, depth, customCopyFunction)) as any as T
        } else {
            const keys = Object.keys(x) as KeysOfUnion<T>[]
            const r: any = {}
            for (const k of keys) {
                const value = x[k]
                if (customCopyFunction) {
                    const customCopiedValue = customCopyFunction(k, x[k])
                    if (customCopiedValue !== undefined) {
                        r[k] = customCopiedValue
                        continue
                    }
                }
                r[k] = deepCopy(value, depth, customCopyFunction)
            }
            return r as T
        }
    } else {
        return x
    }
}

export function getChangedFields<T extends object>(a: T, b: T | undefined): Set<keyof T> {
    const keys = Object.keys(a) as (keyof T)[]
    if (b === undefined) {
        return new Set<keyof T>(keys)
    }
    const changed = new Set<keyof T>()
    for (const k of keys) {
        if (!deepEqual(a[k], b[k])) {
            changed.add(k)
        }
    }
    return changed
}

export function isEmptyObject(obj: any) {
    for (const prop in obj) {
        if (obj.hasOwnProperty(prop)) {
            return false
        }
    }
    return true
}

export class TwoWayReadonlyMap<T, K> {
    map: Map<T, K>
    reverseMap: Map<K, T>

    constructor(map: Map<T, K>) {
        this.map = map
        this.reverseMap = new Map<K, T>()
        map.forEach((value, key) => {
            this.reverseMap.set(value, key)
        })
    }

    get(key: T) {
        return this.map.get(key)
    }

    reverseGet(key: K) {
        return this.reverseMap.get(key)
    }
}

export type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never

export function mostCommonValue<T>(array: ArrayLike<T> & Iterable<T>): T | null {
    if (array.length == 0) return null
    const modeMap = new Map<T, number>()
    let curVal = array[0],
        curCt = 1
    for (const val of array) {
        const ct = (modeMap.get(val) ?? 0) + 1
        modeMap.set(val, ct)
        if (ct > curCt) {
            curVal = val
            curCt = ct
        }
    }
    return curVal
}

const idCounterCharTable = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"

export class CompactUIDGenerator {
    private curIdCounter = 0

    reset(): void {
        this.curIdCounter = 0
    }

    next(): string {
        return (this.curIdCounter++ | 0).toString()
        // let str = "";
        // let bits = this.curIdCounter++ | 0;
        // const numChars = idCounterCharTable.length;
        // while (bits > 0) {
        //     const idx = bits % numChars;
        //     bits = (bits / numChars) | 0;
        //     str += idCounterCharTable[idx];
        // }
        // return str;
    }
}

export class CompactUid<K extends object> {
    private gen = new CompactUIDGenerator()
    private map = new WeakMap<K, string>()

    intern(k: K): string {
        let v = this.map.get(k)
        if (v === undefined) {
            v = this.gen.next()
            this.map.set(k, v)
        }
        return v
    }
}

export class CompactUIDTable<K> {
    private gen = new CompactUIDGenerator()
    private map = new Map<K, string>()

    clear(): void {
        this.gen.reset()
        this.map.clear()
    }

    intern(k: K): string {
        let v = this.map.get(k)
        if (v === undefined) {
            v = this.gen.next()
            this.map.set(k, v)
        }
        return v
    }
}

export class WeakCompactUIDTable<K extends object> {
    private gen = new CompactUIDGenerator()
    private map = new WeakMap<K, string>()

    intern(k: K): string {
        let v = this.map.get(k)
        if (v === undefined) {
            v = this.gen.next()
            this.map.set(k, v)
        }
        return v
    }
}

export function castToUint8Array(x: ArrayBuffer | ArrayBufferView) {
    if (ArrayBuffer.isView(x)) {
        return new Uint8Array(x.buffer, x.byteOffset, x.byteLength)
    } else {
        return new Uint8Array(x)
    }
}

export function castToUint16Array(x: ArrayBuffer | ArrayBufferView): Uint16Array {
    if (ArrayBuffer.isView(x)) {
        return new Uint16Array(x.buffer, x.byteOffset, Math.floor(x.byteLength / 2))
    } else {
        return new Uint16Array(x)
    }
}

export function castToUint32Array(x: ArrayBuffer | ArrayBufferView): Uint32Array {
    if (ArrayBuffer.isView(x)) {
        return new Uint32Array(x.buffer, x.byteOffset, Math.floor(x.byteLength / 4))
    } else {
        return new Uint32Array(x)
    }
}

export function castToFloat32Array(x: ArrayBuffer | ArrayBufferView): Float32Array {
    if (ArrayBuffer.isView(x)) {
        return new Float32Array(x.buffer, x.byteOffset, Math.floor(x.byteLength / 4))
    } else {
        return new Float32Array(x)
    }
}

export function halfToFloatArray(x: ArrayBuffer | ArrayBufferView): Float32Array {
    const halfArr = castToUint16Array(x)
    const length = halfArr.length
    const floatArr = new Uint32Array(length)
    for (let n = 0; n < length; n++) {
        const x = halfArr[n]
        floatArr[n] = ((x & 0x8000) << 16) | (((x & 0x7c00) + 0x1c000) << 13) | ((x & 0x03ff) << 13)
    }
    return castToFloat32Array(floatArr)
}

export function floatToHalfArray(x: ArrayBuffer | ArrayBufferView): Uint16Array {
    const floatArr = castToUint32Array(x)
    const length = floatArr.length
    const halfArr = new Uint16Array(length)
    for (let n = 0; n < length; n++) {
        const x = floatArr[n]
        // halfArr[n] = ((x >> 16) & 0x8000) | ((((x & 0x7f800000) - 0x38000000) >> 13) & 0x7c00) | ((x >> 13) & 0x03ff)
        const b = x + 0x00001000 // round-to-nearest-even: add last bit after truncated mantissa
        const e = (b & 0x7f800000) >> 23 // exponent
        const m = b & 0x007fffff // mantissa; in line below: 0x007FF000 = 0x00800000-0x00001000 = decimal indicator flag - initial rounding
        // sign : normalized : denormalized : saturate
        let h = (b & 0x80000000) >> 16
        if (e > 112) h |= (((e - 112) << 10) & 0x7c00) | (m >> 13)
        if (e < 113 && e > 101) h |= (((0x007ff000 + m) >> (125 - e)) + 1) >> 1
        if (e > 143) h |= 0x7fff
        halfArr[n] = h
    }
    return halfArr
}

export function trimDecimalPlaces(value: number, places: number) {
    let factor = 1
    while (places--) factor *= 10
    return Math.round(value * factor) / factor
}

export function zip<A, B>(a: A[], b: B[]): [A, B][] {
    return a.map((x, i) => [x, b[i]])
}

export function mapFieldNames<
    T extends
        | ArrayLike<unknown>
        | {
              [s: string]: unknown
          },
>(x: T, fn: (fieldName: string) => string): {[s: string]: T[keyof T]} {
    const r: any = {}
    for (const [k, v] of Object.entries(x)) {
        r[fn(k)] = v
    }
    return r
}

export function filterFieldNames<
    T extends
        | ArrayLike<unknown>
        | {
              [s: string]: unknown
          },
>(x: T, fn: (fieldName: string) => boolean): {[s: string]: T[keyof T]} {
    const r: any = {}
    for (const [k, v] of Object.entries(x)) {
        const keepKey = fn(k)
        if (keepKey) {
            r[k] = v
        }
    }
    return r
}

export function findFieldName<
    T extends
        | ArrayLike<unknown>
        | {
              [s: string]: unknown
          },
>(x: T, fn: (fieldName: string) => boolean): T[keyof T] | undefined {
    for (const [k, v] of Object.entries(x)) {
        if (fn(k)) {
            return v as T[keyof T]
        }
    }
    return undefined
}

type PlainObj = Record<string, unknown>
export type PromisesMap<T extends PlainObj> = {
    [P in keyof T]: Promise<T[P]> | T[P]
}

type TupleEntry<T extends readonly unknown[], I extends unknown[] = [], R = never> = T extends readonly [infer Head, ...infer Tail]
    ? TupleEntry<Tail, [...I, unknown], R | [`${I["length"]}`, Head]>
    : R

type ObjectEntry<T extends {}> = T extends object
    ? {
          [K in keyof T]: [K, Required<T>[K]]
      }[keyof T] extends infer E
        ? E extends [infer K, infer V]
            ? K extends string | number
                ? [`${K}`, V]
                : never
            : never
        : never
    : never

type TypedObjectEntry<T extends {}> = T extends readonly [unknown, ...unknown[]]
    ? TupleEntry<T>
    : T extends ReadonlyArray<infer U>
      ? [`${number}`, U]
      : ObjectEntry<T>

export function typedEntries<K extends string | number | symbol, V>(record: Record<K, V>): ReadonlyArray<readonly [K, V]>
export function typedEntries<T extends {}>(object: T): ReadonlyArray<TypedObjectEntry<T>>
export function typedEntries(object: any): any {
    return Object.entries(object)
}

export function bytesToSize(bytes: number): string {
    const sizes = ["B", "KB", "MB", "GB", "TB"]
    if (bytes === 0) {
        return "0 B"
    }
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i]
}

export function assertNever(x: never): never {
    throw new Error(`Unexpected object: ${x}`)
}

export const removeUndefinedValues = <ObjectType extends object>(value: ObjectType) =>
    Object.fromEntries(Object.entries(value).filter(([_, value]) => value !== undefined)) as {[Key in keyof ObjectType]-?: ObjectType[Key]}

export function removeUndefinedEntriesFromObject<T extends object>(obj: T): T {
    const clonedObject = deepCopy(obj)

    function deleteUndefinedEntries(obj: any): any {
        if (obj && typeof obj === "object") {
            Object.keys(obj).forEach((key: string) => {
                if (obj[key] === undefined) {
                    delete obj[key]
                } else if (typeof obj[key] === "object" && obj[key] !== null) {
                    deleteUndefinedEntries(obj[key])
                }
            })
        }
    }

    deleteUndefinedEntries(clonedObject)
    return clonedObject
}

export function recursivelyReplaceSubObjects(obj: object, objectMapper: (obj: object) => object | void) {
    const doReplace = (obj: object) => {
        let numReplacements = 0

        const visitedNodes = new Set<object>()
        const replaceSubObjects = (parameter: unknown): object | void => {
            if (typeof parameter !== "object" || !parameter || visitedNodes.has(parameter)) return

            const mappedObject = objectMapper(parameter)

            if (typeof mappedObject === "object") {
                visitedNodes.add(mappedObject)
                numReplacements++
                replaceSubObjects(mappedObject)
                return mappedObject
            } else if (typeof parameter === "object" && parameter) {
                visitedNodes.add(parameter)
                if (parameter instanceof Array) {
                    for (let i = 0; i < parameter.length; i++) {
                        const mappedParameter = replaceSubObjects(parameter[i])
                        if (typeof mappedParameter === "object") parameter[i] = mappedParameter
                    }
                } else if (!isClass(parameter)) {
                    for (const [key, value] of Object.entries(parameter)) {
                        const mappedParameter = replaceSubObjects(value)
                        if (typeof mappedParameter === "object") (parameter as Record<string, unknown>)[key] = mappedParameter
                    }
                }
            }
        }

        replaceSubObjects(obj)
        return numReplacements
    }

    for (let i = 0; i < 10; i++) {
        if (doReplace(obj) === 0) return
    }

    throw new Error("recursivelyReplaceSubObjects: too many iterations")
}

export async function promiseAllProperties<T extends PlainObj>(promisesMap: PromisesMap<T>): Promise<T> {
    const keys = Object.keys(promisesMap) as (keyof T)[]
    const promises = keys.map((key) => {
        return promisesMap[key]
    })
    const results = await Promise.all(promises)

    return results.reduce((resolved, result, index) => {
        resolved[keys[index]] = result
        return resolved
    }, {} as T)
}

export function mapFields<
    ArrayOrObject extends
        | ArrayLike<unknown>
        | {
              [s: string]: unknown
          },
    Mapper extends (value: ArrayOrObject[keyof ArrayOrObject], key: keyof ArrayOrObject) => any,
>(arrayOrObject: ArrayOrObject, mapper: Mapper): {[Key in keyof ArrayOrObject]: ReturnType<Mapper>} {
    if (Array.isArray(arrayOrObject)) {
        const result: Array<ReturnType<Mapper>> = new Array(arrayOrObject.length)
        for (let i = 0; i < arrayOrObject.length; i++) {
            result[i] = mapper(arrayOrObject[i], i)
        }
        return result as {[Key in keyof ArrayOrObject]: ReturnType<Mapper>}
    } else {
        const result: Partial<{[Key in keyof ArrayOrObject]: ReturnType<Mapper>}> = {}
        for (const [key, value] of Object.entries(arrayOrObject)) {
            result[key as keyof ArrayOrObject] = mapper(value as ArrayOrObject[keyof ArrayOrObject], key as keyof ArrayOrObject)
        }
        return result as {[Key in keyof ArrayOrObject]: ReturnType<Mapper>}
    }
}

export function filterFields<
    ArrayOrObject extends
        | ArrayLike<unknown>
        | {
              [s: string]: unknown
          },
    Filter extends (value: ArrayOrObject[keyof ArrayOrObject], key: keyof ArrayOrObject) => boolean,
>(arrayOrObject: ArrayOrObject, filter: Filter): Partial<{[Key in keyof ArrayOrObject]: any}> {
    const result: Partial<{[Key in keyof ArrayOrObject]: any}> = {}
    for (const [key, value] of Object.entries(arrayOrObject)) {
        if (filter(value as ArrayOrObject[keyof ArrayOrObject], key as keyof ArrayOrObject)) {
            result[key as keyof ArrayOrObject] = value as ArrayOrObject[keyof ArrayOrObject]
        }
    }
    return result
}

export function mapTuple<T extends [any, ...any[]], U>(tuple: T, fn: (value: T[number], index: number) => U): {[K in keyof T]: U} {
    return tuple.map((v, i) => fn(v, i)) as {[K in keyof T]: U}
}

export async function parallelMapOptionalFields<T extends Record<string, V | undefined>, U, V>(
    obj: T,
    fn: (value: Exclude<V, undefined>, key: keyof T) => Promise<U>,
): Promise<{[K in keyof T]: undefined extends T[K] ? U | undefined : U}> {
    const keys = Object.keys(obj) as (keyof T)[]
    const pendingValues: Promise<U | undefined>[] = keys.map((key) => {
        const value = obj[key]
        return value === undefined ? Promise.resolve(undefined) : fn(value as Exclude<V, undefined>, key)
    })
    const values = await Promise.all(pendingValues)
    return Object.fromEntries(keys.map((key, idx) => [key, values[idx]])) as {
        [K in keyof T]: undefined extends T[K] ? U | undefined : U
    }
}
